/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
class RestService {
    constructor($q, resource, $httpParamSerializerJQLike, $log, cacheEnabled) {
        this.$q = $q;
        this.resource = resource;
        this.$httpParamSerializerJQLike = $httpParamSerializerJQLike;
        this.$log = $log;
        this.cacheSettings = {
            enabled: cacheEnabled,
            reloadBeforeWriteEnabled: false
        };
        this.cache = [];
        this.loaded = false;
        this.$log.debug('Service \'RestService\' loaded');
    }

    /**
     * Request the retrieve of a record. The cache is reloaded and the record is searched in.
     *
     * @param {Number} id Entity ID.
     * @return {Promise} A promise whose data is the entity.
     */
    getEntity(id) {
        return this.resource.get({id}).$promise;
    }

    /**
     * Request a creation, and if successful, adds it to the cache (if enabled).
     *
     * @param {Object} data The data to be created.
     * @returns {Promise} A promise whose data shall be the created record.
     */
    createEntity(data) {
        return this.reloadCacheBeforeWrite().then(() => this.resource.save(data).$promise).then(record => {
            if (this.cacheSettings.enabled) {
                this.cache.push(record);
            }
            return record;
        });
    }

    /**
     * Request a creation with file upload, and if successful, adds it to the cache (if enabled).
     *
     * @param {Object} data The data to be created.
     * @returns {Promise} A promise whose data shall be the created record.
     */
    createEntityWithUpload(params, data) {
        const formData = new FormData();
        for (const property in data) {
            if (angular.isArray(data[property]) || (data[property] instanceof FileList)) {
                for (let i = 0; i < data[property].length; i++) {
                    formData.append(property, data[property][i]);
                }
            } else {
                formData.append(property, data[property]);
            }
        }
        return this.reloadCacheBeforeWrite().then(() => this.resource.saveAndUpload(params, formData).$promise)
            .then(record => {
                if (this.cacheSettings.enabled) {
                    this.cache.push(record);
                }
                return record;
            }
        );
    }

    /**
     * Request
     */
    getAllEntities() {
        return this.reloadCache(false);
    }

    /**
     * Sends a request to delete a record.
     *
     * @param {Number|Object} id Entity ID, or entity IDs if multiple IDs must be specified in the query parameters.
     * @return {Promise} A promise whose data shall be the record.
     */
    deleteEntity(id) {
        const queryParams = angular.isObject(id) ? id : {id};
        return this.resource.delete(queryParams).$promise;
    }

    /**
     * Search entities using the given parameters, with automatic pagination.
     *
     * @param {Object} [queryData] Data to be sent in the request: may contain a 'params' key with an array of query
     * parameters, and a 'data' key with an object to be sent in the request body.
     * @return {Promise} A promise with the entities found (paginated).
     */
    searchEntities(queryData) {
        let params;
        let data;
        if (queryData) {
            ({params, data} = queryData);
        }
        return this.resource.search(params, data).$promise;
    }

    /**
     * Find entities using the given data.
     *
     * @param {Object} [queryData] Data to be sent in the request: must contain a 'params' key with an array of query
     * parameters, and a 'data' key with an object to be sent in the request body.
     * @return {Promise} A promise with the entities found.
     */
    findEntities(queryData) {
        let params;
        let data;
        if (queryData) {
            ({params, data} = queryData);
        }
        return this.resource.find(params, data).$promise;
    }

    /**
     * Find entities 'light' using the given data.
     *
     * @param {Object} [queryData] Data to be sent in the request: must contain a 'params' key with an array of query
     * parameters, and a 'data' key with an object to be sent in the request body.
     * @return {Promise} A promise with the entities found.
     */
    findLightEntities(queryData) {
        let params;
        let data;
        if (queryData) {
            ({params, data} = queryData);
        }
        return this.resource.findLight(params, data).$promise;
    }

    /**
     * Count entities using the given data.
     *
     * @param {Object} [queryData] Data to be sent in the request: must contain a 'params' key with an array of query
     * parameters, and a 'data' key with an object to be sent in the request body.
     * @return {Promise} A promise with the count of entities found.
     */
    countEntities(queryData) {
        let params;
        let data;
        if (queryData) {
            ({params, data} = queryData);
        }
        return this.resource.count(params, data).$promise;
    }

    /**
     * Request the update of a record, and, if successful, replaces the instance in the store.
     *
     * @param {Object} data The data to be updated.
     * @return {Promise} A promise whose data shall be the updated object.
     */
    updateEntity(data) {
        // When the cache is reloaded, send the update request to the server.
        return this.reloadCacheBeforeWrite().then(() => this.resource.update({id: data.id}, data).$promise).then(() => {
            // Update the data in the store, do not reload all the store for performance reason.
            if (this.cacheSettings.enabled) {
                for (let i = 0; i < this.cache.length; i++) {
                    if (this.cache[i].id === data.id) {
                        this.cache[i] = data;
                        break;
                    }
                }
            }
            return data;
        });
    }

    /**
     * Request a creation with file upload, and if successful, adds it to the cache (if enabled).
     *
     * @param {Object} data The data to be created.
     * @returns {Promise} A promise whose data shall be the created record.
     */
    updateEntityWithUpload(params, data) {
        const formData = new FormData();
        for (const property in data) {
            if (angular.isArray(data[property]) || (data[property] instanceof FileList)) {
                for (let i = 0; i < data[property].length; i++) {
                    formData.append(property, data[property][i]);
                }
            } else {
                formData.append(property, data[property]);
            }
        }
        return this.reloadCacheBeforeWrite().then(() => this.resource
            .updateAndUpload({id: params.id}, formData).$promise)
            .then(record => {
                if (this.cacheSettings.enabled) {
                    this.cache.push(record);
                }
                return record;
            });
    }

    patchEntity(params, data) {
        return this.reloadCacheBeforeWrite().then(() => this.resource.patch(params, data).$promise).then(() => {
            // Update the data in the store, do not reload all the store for performance reason.
            if (this.cacheSettings.enabled) {
                for (let i = 0; i < this.cache.length; i++) {
                    if (this.cache[i].id === data.id) {
                        this.cache[i] = data;
                        break;
                    }
                }
            }
            return data;
        });
    }

    /**
     * This private method shall not be called from the outside, and is reserved for internal implementation. It is used
     * on create/update actions, to reload the cache before the request is sent to the server.
     *
     * @return {Promise} A promise resolved with the cache data (null if disabled).
     */
    reloadCacheBeforeWrite() {
        let promise;
        if (this.cacheSettings.reloadBeforeWriteEnabled) {
            promise = this.reloadCache(false);
        } else {
            promise = this.$q.resolve();
        }
        return promise;
    }

    /**
     * Reloads data in cache, if it has not been loaded yet.
     *
     * @param {Boolean} forced True to force loading even if the data have already been loaded.
     * @return {Promise} A promise with the cache content as only argument.
     */
    reloadCache(forced) {
        let promise;
        if (!this.loaded || forced || !this.cacheSettings.enabled) {
            promise = this.resource.query().$promise.then(data => {
                if (this.cacheSettings.enabled) {
                    this.cache = data;
                    this.loaded = true;
                }
                return data;
            }).catch(response => {
                if ((response.status === 401) || (response.status === 403)) {
                    return this.$q.resolve(response.data);
                } else {
                    return this.$q.reject(response);
                }
            });
        } else {
            // We create a promise that is immediately resolved as the store is already loaded.
            promise = this.$q.resolve(this.cache);
        }
        return promise;
    }
}

export { RestService };



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/RestService.js